import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { createClient, Session } from '@supabase/supabase-js';
import Vapi from '@vapi-ai/web';

// ─── SUPABASE CLIENT ─────────────────────────────────────────────────────────
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

// ─── VAPI CLIENT ─────────────────────────────────────────────────────────────
const VAPI_KEY = process.env.REACT_APP_VAPI_PUBLIC_KEY || '';
export const vapi = new Vapi(VAPI_KEY);

// ─── JAKE ALERT TYPE ─────────────────────────────────────────────────────────
export interface JakeAlert {
  id: string;
  level: 'RED' | 'YELLOW' | 'GREEN';
  message: string;
  load_number?: string;
  driver_name?: string;
  timestamp: Date;
  acknowledged: boolean;
}

// ─── CONTEXT SHAPE ───────────────────────────────────────────────────────────
interface TMSContextType {
  session: Session | null;
  userId: string;
  companyId: string;
  companyName: string;
  companyLoading: boolean;
  isJakeActive: boolean;
  jakeAlerts: JakeAlert[];
  loadsCache: any[];
  driversCache: any[];
  setIsJakeActive: (v: boolean) => void;
  addJakeAlert: (alert: Omit<JakeAlert, 'id' | 'timestamp' | 'acknowledged'>) => void;
  acknowledgeAlert: (id: string) => void;
  refreshLoadsCache: () => Promise<void>;
  refreshDriversCache: () => Promise<void>;
  playAlertSound: (level: 'RED' | 'YELLOW') => void;
}

const TMSContext = createContext<TMSContextType | null>(null);

export function useTMS() {
  const ctx = useContext(TMSContext);
  if (!ctx) throw new Error('useTMS must be used inside TMSProvider');
  return ctx;
}

// ─── PROVIDER ────────────────────────────────────────────────────────────────
export function TMSProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [userId, setUserId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyLoading, setCompanyLoading] = useState(true);
  const [isJakeActive, setIsJakeActive] = useState(false);
  const [jakeAlerts, setJakeAlerts] = useState<JakeAlert[]>([]);
  const [loadsCache, setLoadsCache] = useState<any[]>([]);
  const [driversCache, setDriversCache] = useState<any[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);

  // ── Auth ──
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUserId(data.session?.user?.id || '');
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      setUserId(s?.user?.id || '');
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  // ── Fetch company ID from user profile ──
  useEffect(() => {
    if (!userId) return;
    setCompanyLoading(true);
    supabase.from('profiles').select('company_id').eq('user_id', userId).maybeSingle()
      .then(({ data }) => {
        if (data?.company_id) {
          setCompanyId(data.company_id);
          supabase.from('companies').select('name').eq('id', data.company_id).maybeSingle()
            .then(({ data: co }) => { if (co?.name) setCompanyName(co.name); });
        }
        setCompanyLoading(false);
      });
  }, [userId]);

  // ── Loads cache ──
  const refreshLoadsCache = useCallback(async () => {
    if (!companyId) return;
    const { data } = await supabase
      .from('loads')
      .select('id,load_number,status,workflow_status,profit_band,pickup_city,pickup_state,delivery_city,delivery_state,assigned_driver_id,assigned_asset_id,rate_total,net_profit,profit_margin_percent,rpm,pickup_date,delivery_date,customer_name,pickup_lat,pickup_lng,delivery_lat,delivery_lng,dispatched_at,delivered_at')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });
    if (data) setLoadsCache(data);
  }, [companyId]);

  // ── Drivers cache ──
  const refreshDriversCache = useCallback(async () => {
    if (!companyId) return;
    const { data } = await supabase
      .from('drivers')
      .select('id,full_name,status,hos_status,current_city,current_state,last_lat,last_lng,pay_type,pay_rate_percent,pay_rate_per_mile,assigned_asset_id,cdl_expiration,dot_physical_expiration')
      .eq('company_id', companyId);
    if (data) setDriversCache(data);
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      refreshLoadsCache();
      refreshDriversCache();
    }
  }, [companyId, refreshLoadsCache, refreshDriversCache]);

  // ── Alert sound ──
  const playAlertSound = useCallback((level: 'RED' | 'YELLOW') => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = level === 'RED' ? 880 : 660;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (level === 'RED' ? 1.2 : 0.8));
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + (level === 'RED' ? 1.2 : 0.8));
    } catch { /* audio not available */ }
  }, []);

  // ── Add Jake alert (with dedup — same unacknowledged alert for same load never fires twice) ──
  const addJakeAlert = useCallback((alert: Omit<JakeAlert, 'id' | 'timestamp' | 'acknowledged'>) => {
    setJakeAlerts(prev => {
      const isDupe = prev.some(a =>
        !a.acknowledged &&
        String(a.load_number) === String(alert.load_number) &&
        a.message === alert.message
      );
      if (isDupe) return prev;
      const newAlert: JakeAlert = {
        ...alert,
        id: `alert_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        timestamp: new Date(),
        acknowledged: false,
      };
      if (alert.level === 'RED' || alert.level === 'YELLOW') {
        playAlertSound(alert.level);
      }
      return [newAlert, ...prev].slice(0, 50);
    });
  }, [playAlertSound]);

  const acknowledgeAlert = useCallback((id: string) => {
    setJakeAlerts(prev => prev.map(a => a.id === id ? { ...a, acknowledged: true } : a));
  }, []);

  // VAPI call-state sync — listeners live in App.tsx to avoid duplicate handlers on the shared instance

  return (
    <TMSContext.Provider value={{
      session, userId, companyId, companyName, companyLoading,
      isJakeActive, jakeAlerts,
      loadsCache, driversCache,
      setIsJakeActive, addJakeAlert, acknowledgeAlert,
      refreshLoadsCache, refreshDriversCache,
      playAlertSound,
    }}>
      {children}
    </TMSContext.Provider>
  );
}
